import React, { useMemo } from "react";
import { BarChart } from "@/components/ui/charts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { useReportingData } from "@/hooks/useReportingData";

export interface EnrollmentChartProps {
  className?: string;
  detailed?: boolean;
  dateRange?: { from: Date; to: Date };
}

export function EnrollmentChart({ className, detailed = false, dateRange }: EnrollmentChartProps) {
  const { enrollmentData } = useReportingData(dateRange);

  const totalNew = useMemo(() => enrollmentData.reduce((sum, d) => sum + (d.newStudents || 0), 0), [enrollmentData]);
  const totalWithdrawals = useMemo(() => enrollmentData.reduce((sum, d) => sum + (d.withdrawals || 0), 0), [enrollmentData]);
  const percentChange = useMemo(() => {
    if (enrollmentData.length < 2) return 0;
    const last = enrollmentData[enrollmentData.length - 1]?.newStudents || 0;
    const prev = enrollmentData[enrollmentData.length - 2]?.newStudents || 0;
    if (prev === 0) return last > 0 ? 100 : 0;
    return ((last - prev) / prev) * 100;
  }, [enrollmentData]);

  return (
    <Card className={cn("shadow-lg border-0 bg-gradient-to-br from-white via-green-50 to-green-100/60 backdrop-blur-md p-0", className)}>
      <CardHeader className="pb-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <CardTitle className="text-2xl font-bold text-green-900 flex items-center gap-2">
            Enrollment
            <span className="text-lg font-normal text-green-500">(New & Withdrawals)</span>
          </CardTitle>
          <div className="flex items-baseline gap-4 mt-2">
            <span className="text-3xl font-extrabold text-green-700">{totalNew} new</span>
            <span className="text-2xl font-bold text-red-600">{totalWithdrawals} left</span>
            <span className={cn(
              "text-sm font-semibold px-2 py-1 rounded-full",
              percentChange > 0 ? "bg-green-100 text-green-700" : percentChange < 0 ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"
            )}>
              {percentChange > 0 ? "+" : ""}{percentChange.toFixed(1)}%
            </span>
            <span className="text-xs text-muted-foreground">vs previous period</span>
          </div>
        </div>
        <Users className="h-8 w-8 text-green-400" />
      </CardHeader>
      <CardContent className="pt-0 pb-6">
        <div className="h-80 w-full">
          <BarChart
            data={enrollmentData}
            index="month"
            categories={["newStudents", "withdrawals"]}
            colors={["blue", "red"]}
            valueFormatter={(value) => `${value} students`}
          />
        </div>
      </CardContent>
    </Card>
  );
}
